import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { TableComponent } from '../table/table.component';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpService } from 'src/app/http.service';
import { CalculationDetailComponent } from '../calculation-detail/calculation-detail.component';

@Component({
  selector: 'app-player-transaction-history',
  templateUrl: './player-transaction-history.component.html',
  styleUrls: ['./player-transaction-history.component.scss']
})
export class PlayerTransactionHistoryComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  public history_type = 'bank';
  public player_uuid: any;
  public is_init: boolean = false;
  public date: string;
  public bank_from = '';
  public bank_to = '';
  public transaction_type = '';
  public bank_search = '';
  public record_source = '';
  public filter_error = '';
  public exclude_bets: boolean = false;
  public end_date_mode: string = 'next_deposit';
  // Player Statement / Money Trail — reconciled summary captured from the table reply.
  public statement_summary: any = null;
  // Contextual message shown when an agent/banker-only ledger tab is empty *because the
  // player is not that role* (so the blank table reads as expected, not as a missing-data bug).
  public empty_role_hint: string = '';
  @ViewChild('history') history: TableComponent

  public cached_player_uuid: any;
  @Input() all = true;
  // Role flags of the player being viewed; let us explain an expected-empty Agent/Banker tab.
  // Left null by hosts that don't know the role — the hint then never shows.
  @Input() is_agent: any = null;
  @Input() is_banker: any = null;
  @Input() visible_tabs: string[] = ['statement', 'bank', 'game', 'wallet', 'agent', 'referral', 'banker', 'promotion', 'other'];
  @Input() request_context: any = {};
  @Input() set setPlaterUUID(v: any) {
    this.player_uuid = v;
    this.cached_player_uuid = v;
  };
  @Input() set setHistoryType(v: any) {
    this.history_type = v;
  }

  showHistoryNew(mode: string) {
    if (mode === 'all') {
      this.all = true;
      this.player_uuid = this.cached_player_uuid || null;
    } else if (mode === 'player_all') {
      this.all = true;
      this.player_uuid = this.cached_player_uuid;
    } else if (mode === 'date') {
      this.all = false;
      this.player_uuid = this.cached_player_uuid;
    }
    this.initTable()
  }

  constructor(private cdr: ChangeDetectorRef, private router: Router, private http: HttpService, private route: ActivatedRoute) {
    let date = new Date();
    this.date = date.getFullYear() + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + ("0" + date.getDate()).slice(-2)
    this.bank_from = this.date;
    this.bank_to = this.date;
  }

  ngAfterViewInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['tab'] && this.visible_tabs.includes(params['tab'])) {
        this.history_type = params['tab'];
      }
      this.is_init = true;
      this.initTable()
    });
  }

  dateChange() {
    this.initTable()
  }

  // Capture the reconciled statement summary from the table's POST reply for the cards header.
  onTableData(reply: any) {
    this.statement_summary = (this.history_type === 'statement' && reply) ? reply.summary : null;

    // An empty Agent Wallet / Banker Wallet tab is *expected* for a player who isn't an
    // agent / banker (the agent_wallet ledger only holds commission, commission-withdraw and
    // adjustment rows). Explain it instead of leaving a bare "No Data Available".
    const isEmpty = reply && Array.isArray(reply.collection)
      ? reply.collection.length === 0
      : (!reply || (Array.isArray(reply) && reply.length === 0));
    this.empty_role_hint = '';
    if (isEmpty) {
      if (this.history_type === 'agent' && this.is_agent != null && !Number(this.is_agent)) {
        this.empty_role_hint = 'This player is not an agent, so there are no Agent Wallet (commission) entries. Deposits, withdrawals and seamless bet settlements are shown under the Wallet tab.';
      } else if (this.history_type === 'banker' && this.is_banker != null && !Number(this.is_banker)) {
        this.empty_role_hint = 'This player is not a banker, so there are no Banker Wallet entries.';
      }
    }
  }

  async loadTable(type) {
    if (this.history_type === type) {
      this.initTable();
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: type },
        queryParamsHandling: 'merge'
      });
    }
  }

  async viewBetLog(row: any) {
    const toBusinessDateTimeInput = (date: Date): string => {
      const pad = (n: number): string => ('0' + n).slice(-2);
      const business = new Date(date.getTime() + (8 * 60 * 60 * 1000));
      return `${business.getUTCFullYear()}-${pad(business.getUTCMonth() + 1)}-${pad(business.getUTCDate())}T${pad(business.getUTCHours())}:${pad(business.getUTCMinutes())}`;
    };
    const toDateTimeInput = (ts: any): string => {
      return String(ts || '').replace(' ', 'T').replace('.000Z', '').slice(0, 16);
    };
    const isAfter = (end: string, start: string): boolean => {
      return !!end && !!start && end > start;
    };

    let date_from = toDateTimeInput(row.timestamp_display || row.bet_log_timestamp || row.timestamp || row.create_on);
    let date_to: string;

    if (this.end_date_mode === 'now') {
      date_to = toBusinessDateTimeInput(new Date());
    } else {
      const type = this.end_date_mode === 'next_deposit' ? 'deposit' : 'withdraw';
      const res: any = await this.http.post('/admin/player/history/get/next-timestamp', {
        current_id: row.id,
        timestamp: row.timestamp_query || row.bet_log_timestamp || row.create_on || row.timestamp,
        player_code: row.player_code,
        type
      });

      if (res && res.current_timestamp_display) {
        date_from = toDateTimeInput(res.current_timestamp_display);
      }

      const nextValue = res && (res.next_timestamp_display || res.timestamp || res.next_timestamp);
      const nextTimestamp = nextValue ? toDateTimeInput(nextValue) : '';
      if (!isAfter(nextTimestamp, date_from)) {
        date_to = toBusinessDateTimeInput(new Date());
      } else {
        date_to = nextTimestamp;
      }
    }

    const company_uuid = this.http.getStaticSelectedCompany();
    this.router.navigate(['/lo', company_uuid, 'game-log'], { queryParams: { code: row.player_code, date_from, date_to, date_mode: 'business_utc8' } });
  }

  openCalculation(row: any, ngbModal: any) {
    const modal = ngbModal.open(CalculationDetailComponent, { size: 'lg', centered: true });
    modal.componentInstance.row = row;
  }

  viewGameLog(row: any) {
    let date_from = row.timestamp.replace(".000Z", "").replace(/-/g, "-").replace("T", " ").split(":")[0] + ":" + row.timestamp.split(":")[1];
    let date_to = row.update_timestamp ? row.update_timestamp.replace(".000Z", "").replace(/-/g, "-").replace("T", " ").split(":")[0] + ":" + row.update_timestamp.split(":")[1] : new Date().toISOString().replace(".000Z", "").replace(/-/g, "-").replace("T", " ").split(":")[0] + ":" + new Date().toISOString().split(":")[1];

    // Convert to T format for the component input
    date_from = date_from.replace(" ", "T");
    date_to = date_to.replace(" ", "T");

    const company_uuid = this.http.getStaticSelectedCompany();
    this.router.navigate(['/lo', company_uuid, 'game-log'], { queryParams: { code: row.player_code, date_from: date_from, date_to: date_to, is_promo: true } });
  }

  // Flag (1) or clear (0) a transaction_bank row as an admin CREDIT / ADVANCE. When flagged AND the
  // company setting agent_exclude_advance_from_basis is on, the row is removed from the agent
  // carried-forward-loss / commission basis. Wallet money is NOT touched — this is a tagging only.
  async markAdvance(row: any, flag: number) {
    if (!row || !row.id) { return; }
    const msg = flag
      ? 'Mark this ' + row.type + ' (' + (row.debit || row.credit) + ') as a CREDIT / ADVANCE? It will be excluded from the agent loss / commission basis. (Wallet balance is not changed.)'
      : 'Clear the CREDIT / ADVANCE flag on this ' + row.type + '?';
    if (!confirm(msg)) { return; }
    const s = await this.http.post('/admin/transaction/mark-advance', { transaction_bank_ids: [row.id], is_advance: flag });
    if (s && s['status']) { this.initTable(); }
  }


  async initTable() {

    if (!this.is_init) {
      return;
    }
    this.filter_error = '';
    if (['bank', 'consolidated'].includes(this.history_type) && !this.all && this.bank_from && this.bank_to && this.bank_from > this.bank_to) {
      this.filter_error = 'From date must be on or before To date.';
      return;
    }

    let setting: any;
    if (this.history_type === 'consolidated') {
      setting = {
        post: { url: '/admin/player/history/get/consolidated', data: { player_uuid: this.player_uuid, all: this.all } },
        disabled_pagenat: false,
        fields: [
          { label: 'Date (MYT)', type: 'timestamp', key: 'timestamp' },
          { label: 'Source', type: 'string', key: 'source' },
          { label: 'Account', type: 'string', key: 'account' },
          { label: 'Type', type: 'string', key: 'type' },
          { label: 'In', type: 'price', key: 'debit' },
          { label: 'Out', type: 'price', key: 'credit' },
          { label: 'Bonus', type: 'price', key: 'bonus' },
          { label: 'Account balance', type: 'price', key: 'balance' },
          { label: 'Transaction ID', type: 'string', key: 'transaction_id' },
          { label: 'Reference', type: 'string', key: 'reference' },
          { label: 'Remark', type: 'string', key: 'remark' },
        ]
      };
    } else if (this.history_type == 'statement') {
      // Player Statement / Money Trail — one chronological timeline merging deposits,
      // withdrawals, bet stakes and settlements (each labelled in plain language) plus a
      // reconciled summary header. Ledger convention: Debit = money In, Credit = money Out.
      setting =
      {
        post: { url: "/admin/player/statement/get", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Date', type: 'timestamp', key: 'timestamp' },
          { label: 'Type', type: 'string', key: 'category' },
          { label: 'Description', type: 'string', key: 'description' },
          { label: 'In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Ref', type: 'string', key: 'reference' },
        ]
      }
    } else if (this.history_type == 'bank') {
      setting =
      {
        post: { url: "/admin/player/history/get/bank", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Player Code', type: 'string', key: 'player_code' },
          { label: 'Player Mobile', type: 'string', key: 'player_mobile' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Type', type: 'string', key: 'type' },
          { label: 'Bank', type: 'string', key: 'bank_name' },
          { label: 'Debit/In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Tips', type: 'price', key: 'tips', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Update', type: 'string', key: 'timestamp_display' },
          { label: 'Bet Log', type: 'custom_link', visible_if: (row: any) => row.type === 'topup', func: (row) => { this.viewBetLog(row) } },
          // Admin CREDIT / ADVANCE marker (2026-06-26). Flag a top-up the admin FRONTED into the
          // wallet (or its cash-out) so it is excluded from the agent loss/commission basis. Shows
          // an "ADVANCE ✓" tag when set (click to clear); "Mark Advance" otherwise.
          { label: 'Advance', type: 'custom_link', visible_if: (row: any) => row.is_advance == 1, link_text: 'ADVANCE ✓ (clear)', func: (row) => { this.markAdvance(row, 0) } },
          { label: '', type: 'custom_link', visible_if: (row: any) => row.is_advance != 1 && (row.type === 'topup' || row.type === 'withdraw'), link_text: 'Mark Advance', func: (row) => { this.markAdvance(row, 1) } },
        ]
      }
    } else if (this.history_type == 'wallet') {
      setting =
      {
        post: { url: "/admin/player/history/get/wallet", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Type', type: 'string', key: 'type' },
          { label: 'Game', type: 'string', key: 'game_name' },
          { label: 'Debit/In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Tips', type: 'price', key: 'tips', border_left: true, border_right: true },
          { label: 'Bonus', type: 'price', key: 'bonus', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Update', type: 'timestamp', key: 'timestamp' },
        ]
      }
    } else if (this.history_type == 'game') {
      setting =
      {
        post: { url: "/admin/player/history/get/game", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Type', type: 'string', key: 'type' },
          { label: 'Game', type: 'string', key: 'game_name' },
          { label: 'Platform', type: 'string', key: 'platform' },
          { label: 'Game User ID', type: 'copystring', key: 'username' },
          { label: 'Debit/In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Bonus', type: 'price', key: 'bonus', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Update', type: 'date', key: 'create_on' },
          { label: 'Bet Log', type: 'custom_link', func: (row) => { this.viewBetLog(row) } },
        ]
      }
    } else if (this.history_type == 'referral') {
      setting =
      {
        post: { url: "/admin/player/history/get/referral", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Debit/In', type: 'price', key: 'debit', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Bonus', type: 'string', key: 'remark' },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Calculation', type: 'custom_link', link_text: 'View', visible_if: (row: any) => !!(row.formula_param || row.report_formula_param || row.calculation_detail), func: (row, ngbModal) => { this.openCalculation(row, ngbModal) } },
          { label: 'Date', type: 'string', key: 'effective_date_display', fallback_keys: ['effective_date', 'date', 'timestamp'], format: 'date' },
        ]
      }
    } else if (this.history_type == 'agent') {
      setting = {
        post: { url: "/admin/player/history/get/agent", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Debit/In', type: 'price', key: 'debit', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', digits: '1.3-3', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'remark' },
          { label: 'Trans Ref', type: 'string', key: 'trans_ref' },
          { label: 'Calculation', type: 'custom_link', link_text: 'View', visible_if: (row: any) => !!(row.formula_param || row.report_formula_param || row.calculation_detail), func: (row, ngbModal) => { this.openCalculation(row, ngbModal) } },
          { label: 'Update', type: 'string', key: 'effective_date_display', fallback_keys: ['effective_date', 'date', 'timestamp'], format: 'date' },
        ]
      }
    } else if (this.history_type == 'banker') {
      setting = {
        post: { url: "/admin/player/history/get/banker", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Debit/In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'remark' },
          { label: 'Trans Ref', type: 'string', key: 'trans_ref' },
          { label: 'Update', type: 'date', key: 'timestamp' },
        ]
      }
    } else if (this.history_type == 'promotion') {
      setting = {
        post: { url: "/admin/player/history/get/promotion", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Promotion', type: 'string', key: 'promotion_title' },
          { label: 'Join On', type: 'timestamp', key: 'timestamp' },
          { label: 'End Time', type: 'timestamp', key: 'update_timestamp' },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Balance Before', type: 'price', key: 'balance_before', border_left: true },
          { label: 'Balance After', type: 'price', key: 'balance_after', border_right: true },
          { label: 'Bonus', type: 'price', key: 'free_credit', border_left: true, border_right: true },
          { label: 'Log', type: 'custom_link', func: (row) => { this.viewGameLog(row) } },
        ]
      }
    } else {
      setting =
      {
        post: { url: "/admin/player/history/get/other", data: { player_uuid: this.player_uuid, all: this.all, filter: { date: this.date } } },
        disabled_pagenat: false,
        fields: [
          { label: 'No', type: 'index' },
          { label: 'Admin', type: 'string', key: 'admin_name' },
          { label: 'Type', type: 'string', key: 'type' },
          { label: 'Debit/In', type: 'price', key: 'debit', border_left: true, border_right: true },
          { label: 'Credit/Out', type: 'price', key: 'credit', border_left: true, border_right: true },
          { label: 'Balance', type: 'price', key: 'balance', border_left: true, border_right: true },
          { label: 'Profit', type: 'price', key: 'profit', border_left: true, border_right: true },
          { label: 'Tips', type: 'price', key: 'tips', border_left: true, border_right: true },
          { label: 'Bonus', type: 'price', key: 'bonus', border_left: true, border_right: true },
          { label: 'Remark', type: 'string', key: 'trans_ref' },
          { label: 'Update', type: 'date', key: 'timestamp' },
        ]
      }
    }

    if (this.history_type !== 'promotion' || this.all) {
      setting.filter = { date: this.date };
    }

    if (this.history_type === 'bank') {
      setting.filter = { date_from: this.bank_from, date_to: this.bank_to, transaction_type: this.transaction_type, search: this.bank_search };
    }

    if (this.history_type === 'consolidated') {
      setting.filter = { date_from: this.bank_from, date_to: this.bank_to, transaction_type: this.transaction_type, source: this.record_source };
    }

    setting.post.data = { ...setting.post.data, ...(this.request_context || {}) };

    this.history.init(setting);

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 1000)
  }
}
